import Tooltip, { type TooltipProps } from "@mui/material/Tooltip";
import { useForkRef } from "@mui/material/utils";
import { cloneElement, createElement, type ReactElement, type ReactNode, type Ref } from "react";

/**
 * Whether MUI would draw a tooltip for this title: any number (0 included), or a non-empty value.
 * `undefined`, `null`, `false` and `""` open nothing.
 */
export const hasTooltipTitle = (title: unknown): boolean => typeof title === "number" || !!title;

// The props MUI's Tooltip keeps for itself (the destructuring in @mui/material Tooltip.js, v9): it
// forwards everything else to its child, so without a title only these are dropped.
const TOOLTIP_OWN_PROPS = new Set([
    "arrow",
    "classes",
    "describeChild",
    "disableFocusListener",
    "disableHoverListener",
    "disableInteractive",
    "disableTouchListener",
    "enterDelay",
    "enterNextDelay",
    "enterTouchDelay",
    "followCursor",
    "id",
    "leaveDelay",
    "leaveTouchDelay",
    "onClose",
    "onOpen",
    "open",
    "placement",
    "slotProps",
    "slots",
]);

type Props = Omit<TooltipProps, "title" | "children"> & {
    title?: ReactNode | false;
    children: ReactElement<any>;
    ref?: Ref<any>;
};

/**
 * A grid cell's tooltip, mounted only when there is something to show. Most cells have no title, and
 * an MUI Tooltip with none still runs its hooks, clones the child with listeners and a ref and
 * renders an idle Popper: across a grid's first paint (35 rows x 16 columns) that was hundreds of
 * milliseconds at 4x CPU for nothing on screen.
 *
 * Without a title the child is rendered as the element the Tooltip would have cloned: what a wrapper
 * hands down (a `<Link legacyBehavior>`'s href, onClick and ref) still reaches it, the tooltip-only
 * options are dropped, and the child's own props win, as in MUI's Tooltip.
 */
export default function CellTooltip({ title, children, ref, ...props }: Props) {
    const childProps = children.props as { ref?: Ref<any>; className?: string };
    const handleRef = useForkRef(childProps.ref, ref);
    // createElement rather than JSX: the unit test renders this module under Playwright, whose JSX
    // transform does not produce React elements.
    if (hasTooltipTitle(title)) return createElement(Tooltip, { ...props, ref, title, children } as TooltipProps);
    const passThrough: Record<string, unknown> = {};
    for (const key in props) if (!TOOLTIP_OWN_PROPS.has(key)) passThrough[key] = props[key as keyof typeof props];
    if (!ref && Object.keys(passThrough).length === 0) return children;
    const className = [passThrough.className, childProps.className].filter(Boolean).join(" ") || undefined;
    return cloneElement(children, { ...passThrough, ...childProps, className, ref: handleRef });
}
